import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Truck,
  Clock,
  Search,
  RefreshCw,
  Loader,
  X,
  Tag,
  MapPin,
  Wrench,
  Box,
  PackageCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FulfillmentOrder {
  _id: string;
  orderNumber: string;
  orderType: 'patient' | 'bulk';
  status: string;
  totalAmount: number;
  itemCount: number;
  physician: { _id: string; name: string; clinicName?: string } | null;
  patient?: { _id: string; name: string } | null;
  fulfillmentType?: 'pickup' | 'bulk_ship' | 'patient_ship';
  shippingDestination?: 'patient' | 'clinic';
  trackingInfo?: { trackingNumber?: string; trackingUrl?: string };
  invoiceStatus?: string;
  pharmacyOrderId?: string;
  itemSummary?: Array<{ name: string; quantity: number }>;
  createdAt: string;
  shippedAt?: string;
}

interface WorkingEntry {
  markedAt: string;
  label: string;
}

type WorkingMap = Record<string, WorkingEntry>;

type ColumnKey = 'queued' | 'working' | 'filling' | 'labelCreated' | 'packedReady' | 'shipped' | 'pickup';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  color: string;
  bgHeader: string;
  textHeader: string;
  dotColor: string;
  badgeBg: string;
  badgeText: string;
  icon: React.ReactNode;
}

type TimeGroup = 'today' | 'yesterday' | 'older';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'bloom_fulfillment_working';
const PACKED_STORAGE_KEY = 'bloom_fulfillment_packed';
const REFRESH_INTERVAL = 30_000;
// /admin/orders serves at most 100 per page. The board walks pages up to this
// many orders (it needs the whole set to bucket and amount-filter) and says so
// when the date range holds more.
const BOARD_PAGE_SIZE = 100;
const BOARD_MAX_PAGES = 10;
const WORKING_EXPIRY_MS = 24 * 60 * 60 * 1000;
const EXCLUDED_STATUSES = new Set(['delivered', 'voided', 'cancelled']);

const COLUMNS: ColumnDef[] = [
  {
    key: 'queued',
    label: 'Queued',
    color: 'gray',
    bgHeader: 'bg-gray-100',
    textHeader: 'text-gray-700',
    dotColor: 'bg-gray-400',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-700',
    icon: <Clock className="w-4 h-4" />,
  },
  {
    key: 'working',
    label: 'Being Worked',
    color: 'amber',
    bgHeader: 'bg-amber-100',
    textHeader: 'text-amber-700',
    dotColor: 'bg-amber-400',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    icon: <Wrench className="w-4 h-4" />,
  },
  {
    key: 'filling',
    label: 'Filling',
    color: 'blue',
    bgHeader: 'bg-blue-100',
    textHeader: 'text-blue-700',
    dotColor: 'bg-blue-400',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    icon: <Box className="w-4 h-4" />,
  },
  {
    key: 'labelCreated',
    label: 'Label Created',
    color: 'purple',
    bgHeader: 'bg-purple-100',
    textHeader: 'text-purple-700',
    dotColor: 'bg-purple-400',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
    icon: <Tag className="w-4 h-4" />,
  },
  {
    key: 'packedReady',
    label: 'Packed & Ready',
    color: 'orange',
    bgHeader: 'bg-orange-100',
    textHeader: 'text-orange-700',
    dotColor: 'bg-orange-400',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
    icon: <PackageCheck className="w-4 h-4" />,
  },
  {
    key: 'shipped',
    label: 'Shipped',
    color: 'green',
    bgHeader: 'bg-green-100',
    textHeader: 'text-green-700',
    dotColor: 'bg-green-400',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-700',
    icon: <Truck className="w-4 h-4" />,
  },
  {
    key: 'pickup',
    label: 'Ready for Pickup',
    color: 'teal',
    bgHeader: 'bg-teal-100',
    textHeader: 'text-teal-700',
    dotColor: 'bg-teal-400',
    badgeBg: 'bg-teal-100',
    badgeText: 'text-teal-700',
    icon: <MapPin className="w-4 h-4" />,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadWorkingMap(): WorkingMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: WorkingMap = JSON.parse(raw);
    const now = Date.now();
    const cleaned: WorkingMap = {};
    for (const [id, entry] of Object.entries(parsed)) {
      if (now - new Date(entry.markedAt).getTime() < WORKING_EXPIRY_MS) {
        cleaned[id] = entry;
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    return cleaned;
  } catch {
    return {};
  }
}

function saveWorkingMap(map: WorkingMap) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function loadPackedMap(): WorkingMap {
  try {
    const raw = localStorage.getItem(PACKED_STORAGE_KEY);
    if (!raw) return {};
    const parsed: WorkingMap = JSON.parse(raw);
    const now = Date.now();
    const cleaned: WorkingMap = {};
    for (const [id, entry] of Object.entries(parsed)) {
      if (now - new Date(entry.markedAt).getTime() < WORKING_EXPIRY_MS) {
        cleaned[id] = entry;
      }
    }
    localStorage.setItem(PACKED_STORAGE_KEY, JSON.stringify(cleaned));
    return cleaned;
  } catch {
    return {};
  }
}

function savePackedMap(map: WorkingMap) {
  localStorage.setItem(PACKED_STORAGE_KEY, JSON.stringify(map));
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return `yesterday ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}`;
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
}

function getTimeGroup(dateStr: string): TimeGroup {
  const d = new Date(dateStr);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  if (d >= todayStart) return 'today';
  if (d >= yesterdayStart) return 'yesterday';
  return 'older';
}

const TIME_GROUP_LABELS: Record<TimeGroup, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  older: 'Older',
};

const TIME_GROUP_ORDER: TimeGroup[] = ['today', 'yesterday', 'older'];

function classifyOrder(order: FulfillmentOrder): ColumnKey | null {
  if (EXCLUDED_STATUSES.has(order.status)) return null;

  // Pickup orders go to pickup column exclusively
  if (
    order.fulfillmentType === 'pickup' &&
    (order.status === 'processing' || order.status === 'shipped')
  ) {
    return 'pickup';
  }

  if (
    order.status === 'submitted' ||
    order.status === 'awaiting_payment' ||
    order.status === 'draft'
  ) {
    return 'queued';
  }

  if (order.status === 'processing') {
    if (order.trackingInfo?.trackingNumber) return 'labelCreated';
    return 'filling';
  }

  if (order.status === 'shipped') {
    return 'shipped';
  }

  return null;
}

function groupByTime(orders: FulfillmentOrder[]): Record<TimeGroup, FulfillmentOrder[]> {
  const groups: Record<TimeGroup, FulfillmentOrder[]> = { today: [], yesterday: [], older: [] };
  for (const o of orders) {
    groups[getTimeGroup(o.createdAt)].push(o);
  }
  for (const key of TIME_GROUP_ORDER) {
    groups[key].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function OrderCard({
  order,
  isWorking,
  onToggleWorking,
  isPacked,
  onTogglePacked,
  columnKey,
  onOrderClick,
}: {
  order: FulfillmentOrder;
  isWorking: boolean;
  onToggleWorking: (orderId: string) => void;
  isPacked?: boolean;
  onTogglePacked?: (orderId: string) => void;
  columnKey?: ColumnKey;
  onOrderClick?: (order: FulfillmentOrder) => void;
}) {
  const isPatient = order.orderType === 'patient';
  const isClinicShip = isPatient && order.shippingDestination === 'clinic';
  const typeColor = isClinicShip ? 'purple' : isPatient ? 'emerald' : 'blue';
  const typeBgClass = isClinicShip ? 'bg-purple-50 border-purple-200' : isPatient ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200';
  const typeAccentClass = isClinicShip ? 'bg-purple-500' : isPatient ? 'bg-emerald-500' : 'bg-blue-500';
  const typeLabel = isClinicShip ? 'Patient → Clinic' : isPatient ? 'Patient' : 'Bulk';

  return (
    <div
      onClick={() => onOrderClick?.(order)}
      className={`relative rounded-xl shadow-sm border overflow-hidden transition-shadow hover:shadow-md cursor-pointer ${
        isWorking ? 'ring-2 ring-amber-400' : 'border-gray-200'
      }`}
    >
      {/* Type banner */}
      <div className={`flex items-center justify-between px-3 py-1.5 ${typeBgClass} border-b`}>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${typeAccentClass}`} />
          <span className={`text-[10px] font-semibold uppercase tracking-wide text-${typeColor}-700`}>
            {typeLabel}
          </span>
        </div>
        <span className="text-[10px] text-gray-400 font-mono">{order.orderNumber}</span>
      </div>

      <div className="p-3">
        {/* Clinic + price row */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {order.physician?.clinicName && (
              <div className="font-semibold text-sm text-gray-900 truncate">
                {order.physician.clinicName}
              </div>
            )}
            {order.physician && (
              <div className="text-xs text-gray-600 truncate mt-0.5">
                {order.physician.name}
              </div>
            )}
          </div>
          <span className="text-sm font-bold text-gray-900 flex-shrink-0">
            ${order.totalAmount.toFixed(0)}
          </span>
        </div>

        {/* Items with quantities */}
        {order.itemSummary && order.itemSummary.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {order.itemSummary.map((item, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <span className="flex-shrink-0 w-5 h-5 rounded bg-gray-100 text-gray-600 text-[10px] font-semibold flex items-center justify-center">
                  {item.quantity}
                </span>
                <span className="text-gray-600 truncate">{item.name}</span>
              </div>
            ))}
            {order.itemCount > 3 && (
              <div className="text-[11px] text-gray-400 pl-6">+{order.itemCount - 3} more</div>
            )}
          </div>
        )}

        {/* Tracking */}
        {order.trackingInfo?.trackingNumber && (
          <div className="mt-2">
            <span className="inline-block text-[10px] font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
              {order.trackingInfo.trackingNumber}
            </span>
          </div>
        )}

        {/* Pharmacy order ID */}
        {order.pharmacyOrderId && (
          <div className="mt-1.5">
            <span className="text-[10px] text-gray-400">Rx# </span>
            <span className="text-[10px] font-mono text-gray-500">{order.pharmacyOrderId}</span>
          </div>
        )}

        {/* Time + working toggle */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-gray-400">{timeAgo(order.createdAt)}</span>
          <div className="flex items-center gap-1.5">
            {isWorking && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
              </span>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleWorking(order._id); }}
              className={`flex-shrink-0 p-1 rounded-md transition-colors ${
                isWorking
                  ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                  : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
              }`}
              title={isWorking ? 'Remove from Being Worked' : 'Mark as Being Worked'}
            >
              <Wrench className="w-3.5 h-3.5" />
            </button>
            {onTogglePacked && (columnKey === 'labelCreated' || columnKey === 'packedReady') && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onTogglePacked(order._id); }}
                className={`flex-shrink-0 p-1 rounded-md transition-colors ${
                  isPacked
                    ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                    : 'text-gray-300 hover:text-orange-500 hover:bg-orange-50'
                }`}
                title={isPacked ? 'Unmark as Packed' : 'Mark as Packed & Ready'}
              >
                <PackageCheck className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimeDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-1">
      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

function EmptyColumn({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-gray-300">
      <PackageCheck className="w-8 h-8 mb-2" />
      <span className="text-xs">No orders in {label}</span>
    </div>
  );
}

function ClinicGroupCard({
  clinicName,
  orders,
  physicianName,
  workingMap,
  onToggleWorking,
  packedMap,
  onTogglePacked,
  columnKey,
  onOrderClick,
}: {
  clinicName: string;
  orders: FulfillmentOrder[];
  physicianName: string;
  workingMap: WorkingMap;
  onToggleWorking: (orderId: string) => void;
  packedMap?: WorkingMap;
  onTogglePacked?: (orderId: string) => void;
  columnKey?: ColumnKey;
  onOrderClick?: (order: FulfillmentOrder) => void;
}) {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [expanded, setExpanded] = useState(false);
  const workingCount = orders.filter((o) => !!workingMap[o._id]).length;
  const allWorking = workingCount === orders.length;

  const toggleAllWorking = (e: React.MouseEvent) => {
    e.stopPropagation();
    orders.forEach((o) => onToggleWorking(o._id));
  };

  // Aggregate item quantities across all orders
  const allItems: Record<string, number> = {};
  orders.forEach((o) => {
    (o.itemSummary || []).forEach((item) => {
      allItems[item.name] = (allItems[item.name] || 0) + item.quantity;
    });
  });
  const itemEntries = Object.entries(allItems).slice(0, 3);

  const trackingCount = orders.filter((o) => o.trackingInfo?.trackingNumber).length;

  if (expanded) {
    return (
      <div className="bg-emerald-50/50 rounded-lg p-1.5 space-y-1.5">
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center justify-between w-full px-2 py-1 text-left hover:bg-emerald-100/50 rounded-md transition-colors"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-emerald-800 truncate">{clinicName}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); toggleAllWorking(e); }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                allWorking
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
              }`}
              title={allWorking ? 'Remove all from Being Worked' : 'Mark all as Being Worked'}
            >
              <Wrench className="w-3 h-3" />
              {allWorking ? 'Unmark all' : 'Work all'}
            </button>
            <span className="text-[10px] text-emerald-600">{orders.length} orders — collapse</span>
          </div>
        </button>
        {orders.map((order) => (
          <OrderCard
            key={order._id}
            order={order}
            isWorking={!!workingMap[order._id]}
            onToggleWorking={onToggleWorking}
            isPacked={!!packedMap?.[order._id]}
            onTogglePacked={onTogglePacked}
            columnKey={columnKey}
            onOrderClick={onOrderClick}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl shadow-sm border overflow-hidden transition-shadow hover:shadow-md cursor-pointer border-emerald-200"
      onClick={() => setExpanded(true)}
    >
      {/* Emerald banner — same style as patient but multi */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-50 border-b border-emerald-200">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
            {orders.length} Patient Orders
          </span>
        </div>
        {workingCount > 0 && (
          <span className="text-[10px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
            {workingCount} working
          </span>
        )}
      </div>

      <div className="p-3">
        {/* Clinic name — biggest */}
        <div className="font-semibold text-sm text-gray-900 truncate">{clinicName}</div>

        {/* Physician name */}
        <div className="text-xs text-gray-600 truncate mt-0.5">{physicianName}</div>

        {/* Aggregated dollar amount */}
        <div className="mt-1.5 text-xs font-semibold text-gray-700">
          ${orders.reduce((sum, o) => sum + o.totalAmount, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </div>

        {/* Aggregated items with quantities */}
        {itemEntries.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {itemEntries.map(([name, qty], i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <span className="flex-shrink-0 w-5 h-5 rounded bg-gray-100 text-gray-600 text-[10px] font-semibold flex items-center justify-center">
                  {qty}
                </span>
                <span className="text-gray-600 truncate">{name}</span>
              </div>
            ))}
            {Object.keys(allItems).length > 3 && (
              <div className="text-[11px] text-gray-400 pl-6">+{Object.keys(allItems).length - 3} more products</div>
            )}
          </div>
        )}

        {/* Tracking summary */}
        {trackingCount > 0 && (
          <div className="mt-1.5">
            <span className="inline-block text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
              {trackingCount}/{orders.length} have tracking
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-gray-400">{timeAgo(orders[0].createdAt)}{orders.length > 1 ? ` — ${timeAgo(orders[orders.length - 1].createdAt)}` : ''}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAllWorking}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                allWorking
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
              }`}
              title={allWorking ? 'Remove all from Being Worked' : 'Mark all as Being Worked'}
            >
              <Wrench className="w-3 h-3" />
              {allWorking ? 'Unmark all' : 'Work all'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({
  col,
  orders,
  workingMap,
  onToggleWorking,
  packedMap,
  onTogglePacked,
  onOrderClick,
}: {
  col: ColumnDef;
  orders: FulfillmentOrder[];
  workingMap: WorkingMap;
  onToggleWorking: (orderId: string) => void;
  packedMap?: WorkingMap;
  onTogglePacked?: (orderId: string) => void;
  onOrderClick?: (order: FulfillmentOrder) => void;
}) {
  const grouped = useMemo(() => groupByTime(orders), [orders]);

  return (
    <div className="flex flex-col min-w-[280px] max-w-[320px] flex-shrink-0 rounded-xl bg-gray-50 border border-gray-200">
      {/* Header */}
      <div className={`flex items-center gap-2 px-4 py-3 rounded-t-xl ${col.bgHeader}`}>
        <span className={col.textHeader}>{col.icon}</span>
        <span className={`text-sm font-semibold ${col.textHeader}`}>{col.label}</span>
        <span
          className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${col.badgeBg} ${col.badgeText}`}
        >
          {orders.length}
        </span>
      </div>

      {/* Card list */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        {orders.length === 0 ? (
          <EmptyColumn label={col.label} />
        ) : (
          TIME_GROUP_ORDER.map((tg) => {
            const groupOrders = grouped[tg];
            if (groupOrders.length === 0) return null;

            // Group patient orders by clinic, keep bulk orders standalone
            const clinicGroups: Record<string, FulfillmentOrder[]> = {};
            const standalone: FulfillmentOrder[] = [];

            groupOrders.forEach((order) => {
              if (order.orderType === 'patient' && order.physician?.clinicName) {
                const key = order.physician.clinicName;
                if (!clinicGroups[key]) clinicGroups[key] = [];
                clinicGroups[key].push(order);
              } else {
                standalone.push(order);
              }
            });

            // Sort clinic groups: multi-order clinics first
            const sortedClinicKeys = Object.keys(clinicGroups).sort(
              (a, b) => clinicGroups[b].length - clinicGroups[a].length
            );

            return (
              <React.Fragment key={tg}>
                <TimeDivider label={TIME_GROUP_LABELS[tg]} />
                {sortedClinicKeys.map((clinicName) => {
                  const clinicOrders = clinicGroups[clinicName];
                  if (clinicOrders.length > 1) {
                    return (
                      <ClinicGroupCard
                        key={clinicName}
                        clinicName={clinicName}
                        orders={clinicOrders}
                        physicianName={clinicOrders[0].physician?.name || 'Unknown'}
                        workingMap={workingMap}
                        onToggleWorking={onToggleWorking}
                        packedMap={packedMap}
                        onTogglePacked={onTogglePacked}
                        columnKey={col.key}
                        onOrderClick={onOrderClick}
                      />
                    );
                  }
                  return (
                    <OrderCard
                      key={clinicOrders[0]._id}
                      order={clinicOrders[0]}
                      isWorking={!!workingMap[clinicOrders[0]._id]}
                      onToggleWorking={onToggleWorking}
                      isPacked={!!packedMap?.[clinicOrders[0]._id]}
                      onTogglePacked={onTogglePacked}
                      columnKey={col.key}
                      onOrderClick={onOrderClick}
                    />
                  );
                })}
                {standalone.map((order) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    isWorking={!!workingMap[order._id]}
                    onToggleWorking={onToggleWorking}
                    isPacked={!!packedMap?.[order._id]}
                    onTogglePacked={onTogglePacked}
                    columnKey={col.key}
                    onOrderClick={onOrderClick}
                  />
                ))}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function AdminFulfillment() {
  const [orders, setOrders] = useState<FulfillmentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'patient' | 'bulk'>('all');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [endDate, setEndDate] = useState('');
  const [workingMap, setWorkingMap] = useState<WorkingMap>(loadWorkingMap);
  const [packedMap, setPackedMap] = useState<WorkingMap>(loadPackedMap);
  const [showWorkingOnly, setShowWorkingOnly] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<FulfillmentOrder | null>(null);
  // Orders on the server beyond what the board loaded (0 when it has them all).
  const [truncatedCount, setTruncatedCount] = useState(0);
  const fetchSeq = useRef(0);
  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch orders from API
  const fetchOrders = useCallback(
    async (isRefresh = false) => {
      const mine = ++fetchSeq.current;
      try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const all: FulfillmentOrder[] = [];
        let totalCount = 0;
        for (let page = 1; page <= BOARD_MAX_PAGES; page++) {
          const response = await apiClient.getAdminOrders({
            page,
            limit: BOARD_PAGE_SIZE,
            orderType: typeFilter,
            search: debouncedSearch || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          });
          if (mine !== fetchSeq.current) return;
          if (!response.success || !response.data) {
            if (page === 1) throw new Error(response.error || 'Failed to load orders');
            break;
          }
          all.push(...(response.data.data as unknown as FulfillmentOrder[]));
          totalCount = response.data.pagination?.totalCount ?? all.length;
          if (!response.data.pagination?.hasNextPage) break;
        }

        setOrders(all);
        setTruncatedCount(Math.max(0, totalCount - all.length));
      } catch (err) {
        if (mine !== fetchSeq.current) return;
        console.error('Failed to fetch fulfillment orders', err);
        if (!isRefresh) toast.error('Failed to load orders');
      } finally {
        if (mine === fetchSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [typeFilter, debouncedSearch, startDate, endDate]
  );

  // Initial load and refetch on filter changes
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      fetchOrders(true);
    }, REFRESH_INTERVAL);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchOrders]);

  // Toggle an order in/out of the "being worked" set
  const toggleWorking = useCallback((orderId: string) => {
    setWorkingMap((prev) => {
      const next = { ...prev };
      if (next[orderId]) {
        delete next[orderId];
      } else {
        next[orderId] = { markedAt: new Date().toISOString(), label: '' };
      }
      saveWorkingMap(next);
      return next;
    });
  }, []);

  // Toggle an order in/out of the "packed & ready" set
  const togglePacked = useCallback((orderId: string) => {
    setPackedMap((prev) => {
      const next = { ...prev };
      if (next[orderId]) {
        delete next[orderId];
      } else {
        next[orderId] = { markedAt: new Date().toISOString(), label: '' };
      }
      savePackedMap(next);
      return next;
    });
  }, []);

  // Filter by dollar amount. Client-side is safe here: `orders` is every
  // matching order (pages walked above), not one page of them.
  const amountFilteredOrders = useMemo(() => {
    const min = minAmount ? parseFloat(minAmount) : null;
    const max = maxAmount ? parseFloat(maxAmount) : null;
    if (min === null && max === null) return orders;
    return orders.filter((o) => {
      if (min !== null && o.totalAmount < min) return false;
      if (max !== null && o.totalAmount > max) return false;
      return true;
    });
  }, [orders, minAmount, maxAmount]);

  // Classify orders into kanban columns
  const columnOrders = useMemo(() => {
    const buckets: Record<ColumnKey, FulfillmentOrder[]> = {
      queued: [],
      working: [],
      filling: [],
      labelCreated: [],
      packedReady: [],
      shipped: [],
      pickup: [],
    };

    const workingIds = new Set(Object.keys(workingMap));
    const packedIds = new Set(Object.keys(packedMap));

    for (const order of amountFilteredOrders) {
      const col = classifyOrder(order);
      if (!col) continue;

      // When "Isolate Working" is on, working orders only appear in the Being Worked column
      if (showWorkingOnly && workingIds.has(order._id)) {
        buckets.working.push(order);
        continue;
      }

      // Always add working orders to the Being Worked column
      if (workingIds.has(order._id)) {
        buckets.working.push(order);
      }

      // Packed & Ready: labelCreated orders marked as packed move to packedReady
      if (col === 'labelCreated' && packedIds.has(order._id)) {
        buckets.packedReady.push(order);
      } else if (!showWorkingOnly || !workingIds.has(order._id)) {
        // Add to the real status column
        buckets[col].push(order);
      }
    }

    return buckets;
  }, [amountFilteredOrders, workingMap, packedMap, showWorkingOnly]);

  // Count of all non-excluded orders
  const totalActive = useMemo(() => {
    return amountFilteredOrders.filter((o) => classifyOrder(o) !== null).length;
  }, [amountFilteredOrders]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Fulfillment Board</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {totalActive} active order{totalActive !== 1 ? 's' : ''}
            </p>
            {truncatedCount > 0 && (
              <p className="text-xs text-amber-700 mt-1">
                Showing the newest {orders.length} orders; {truncatedCount} older ones in this range are not on the board. Narrow the date range or search to see them.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search orders..."
                className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
              />
            </div>

            {/* Type filter */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {(['all', 'patient', 'bulk'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    typeFilter === t
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            {/* Date range */}
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {(startDate || endDate) && (
                <button
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Dollar amount filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">$</span>
              <input
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="Min"
                className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">—</span>
              <input
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="Max"
                className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {(minAmount || maxAmount) && (
                <button
                  onClick={() => { setMinAmount(''); setMaxAmount(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Working-only toggle */}
            <button
              onClick={() => setShowWorkingOnly((prev) => !prev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                showWorkingOnly
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
              }`}
              title="Toggle: show working orders only in Being Worked column"
            >
              <Wrench className="w-3.5 h-3.5" />
              Isolate Working
            </button>

            {/* Auto-refresh indicator */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              Auto-refresh 30s
            </div>

            {/* Refresh button */}
            <button
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {COLUMNS.map((col) => {
            const count = columnOrders[col.key].length;
            return (
              <div
                key={col.key}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${col.badgeBg} ${col.badgeText}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${col.dotColor}`} />
                {col.label}
                <span className="font-bold">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Refreshing indicator */}
      {refreshing && (
        <div className="flex-shrink-0 px-6">
          <div className="h-0.5 bg-blue-500 animate-pulse rounded-full" />
        </div>
      )}

      {/* Kanban board */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <Loader className="w-8 h-8 animate-spin" />
            <span className="text-sm">Loading fulfillment board...</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto px-6 pb-6 min-h-0">
          <div className="flex gap-4 h-full">
            {COLUMNS.map((col) => (
              <KanbanColumn
                key={col.key}
                col={col}
                orders={columnOrders[col.key]}
                workingMap={workingMap}
                onToggleWorking={toggleWorking}
                packedMap={packedMap}
                onTogglePacked={togglePacked}
                onOrderClick={setSelectedOrder}
              />
            ))}
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} />
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedOrder.orderNumber}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    selectedOrder.orderType === 'patient' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                  }`}>{selectedOrder.orderType === 'patient' ? 'Patient' : 'Bulk'}</span>
                  <span className="text-xs text-gray-500 capitalize">{selectedOrder.status}</span>
                </div>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {selectedOrder.physician && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Physician & Clinic</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedOrder.physician.clinicName}</div>
                  <div className="text-sm text-gray-600">{selectedOrder.physician.name}</div>
                </div>
              )}
              {selectedOrder.patient && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Patient</div>
                  <div className="text-sm text-gray-900">{selectedOrder.patient.name}</div>
                </div>
              )}
              {selectedOrder.itemSummary && selectedOrder.itemSummary.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Items ({selectedOrder.itemCount})</div>
                  {selectedOrder.itemSummary.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="flex-shrink-0 w-6 h-6 rounded bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center">{item.quantity}</span>
                      {item.name}
                    </div>
                  ))}
                  {selectedOrder.itemCount > 3 && <div className="text-sm text-gray-400 pl-8">+{selectedOrder.itemCount - 3} more</div>}
                </div>
              )}
              {selectedOrder.pharmacyOrderId && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Pharmacy Rx#</div>
                  <div className="text-sm font-mono text-gray-700">{selectedOrder.pharmacyOrderId}</div>
                </div>
              )}
              {selectedOrder.trackingInfo?.trackingNumber && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tracking</div>
                  <div className="text-sm font-mono text-purple-600">
                    {selectedOrder.trackingInfo.trackingUrl
                      ? <a href={selectedOrder.trackingInfo.trackingUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{selectedOrder.trackingInfo.trackingNumber}</a>
                      : selectedOrder.trackingInfo.trackingNumber}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created</div>
                  <div className="text-sm text-gray-700">{new Date(selectedOrder.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Total</div>
                  <div className="text-sm font-semibold text-gray-900">${selectedOrder.totalAmount.toFixed(2)}</div>
                </div>
                {selectedOrder.fulfillmentType && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Fulfillment</div>
                    <div className="text-sm text-gray-700 capitalize">{selectedOrder.fulfillmentType.replace('_', ' ')}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-3 bg-gray-50 border-t flex items-center justify-between">
              <button onClick={() => { setSelectedOrder(null); navigate(`/admin/orders?search=${encodeURIComponent(selectedOrder.orderNumber)}`); }} className="text-sm text-blue-600 hover:text-blue-700 font-medium">View in All Orders</button>
              <button onClick={() => setSelectedOrder(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminFulfillment;
